import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import './TrialLanding.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession } from '../utils/authUtils';
import CheckoutModal from '../components/Payment/CheckoutModal';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Paper
} from '@mui/material';
import { API_BASE_URL } from '../config/api';

export default function TrialLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutProps, setCheckoutProps] = useState({ planId: 'basic', billingCycle: 'monthly', amount: 1, isTrial: true });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const pricing = {
    basic: 1199,
    advanced: 1999,
    pro: 2999
  };
  const plans = [
    { id: 'basic', title: 'Basic', bullets: ['Visibility dashboard', 'Basic reports', '1 GMB profile'] },
    { id: 'advanced', title: 'Advanced', bullets: ['Advanced analytics', 'Reply templates', '5 GMB profiles'] },
    { id: 'pro', title: 'Pro', bullets: ['Team seats', 'Priority support', 'Unlimited profiles'] }
  ];

  const [serverPrices, setServerPrices] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/payments/prices`);
        if (!resp.ok) return;
        const json = await resp.json();
        if (!mounted) return;
        if (json && json.success && json.data && Array.isArray(json.data.plans)) {
          const byId = {};
          json.data.plans.forEach(p => { byId[p.id] = p; });
          setServerPrices(byId);
        }
      } catch (e) {
        // ignore fetch errors
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    try {
      const session = getSession();
      const params = new URLSearchParams(location.search || '');
      const token = params.get('trialToken') || params.get('trialtoken') || params.get('token');
      const sig = params.get('sig') || params.get('signature');
      const locationId = params.get('locationId') || params.get('location');
      const source = params.get('source') || params.get('utm_source') || null;
      // If no explicit token in URL, allow showing the landing page (user may have come from Pricing)

      // If not logged in and token present, redirect to login so user comes back here
      if (!session) {
        const source = params.get('source') || params.get('utm_source') || null;
        const next = `/trial-landing?trialToken=${encodeURIComponent(token)}` + (sig ? `&sig=${encodeURIComponent(sig)}` : '') + (locationId ? `&location=${encodeURIComponent(locationId)}` : '') + (source ? `&source=${encodeURIComponent(source)}` : '');
        // Persist invite client-side before redirecting so it survives the login roundtrip
        try {
          const invite = { token, sig: sig || null, locationId: locationId || null, source: source || null, ts: Date.now() };
          const inviteStr = JSON.stringify(invite);
          try { sessionStorage.setItem('trial_invite', inviteStr); } catch(_) {}
          try { localStorage.setItem('trial_invite', inviteStr); } catch(_) {}
          try { window.dispatchEvent(new Event('trialInviteSet')); } catch(_) {}
        } catch (e) {}
        window.location.href = `/login?next=${encodeURIComponent(next)}`;
      }
      else {
        // If user is logged in and arrived with a token, store a short-lived session flag
        try {
          const invite = { token, sig: sig || null, locationId: locationId || null, source: source || null, ts: Date.now() };
          const inviteStr = JSON.stringify(invite);
          try { sessionStorage.setItem('trial_invite', inviteStr); } catch(_) {}
          try { localStorage.setItem('trial_invite', inviteStr); } catch(_) {}
          try { window.dispatchEvent(new Event('trialInviteSet')); } catch(_) {}
          // show the compact offer snackbar instead of the full landing UI
          setCheckoutOpen(false);
          setShowPlanSelector(false);
          setResult(null);
        } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  // Always render the full landing page UI so Pricing -> Start Trial opens it directly
  return (
    <>
      <div className="tl-page">
        <div className="tl-bg" />
        <div className="tl-container single">
          <div className="tl-single-card">
            <div className="tl-top">
              <div className="tl-badge">LIMITED TIME TRIAL OFFER</div>
              <h1 className="tl-title">Get Full Premium Access</h1>
              <div className="tl-sub">Unlock all premium features of Visibeen for 7 days. No commitment. Cancel anytime.</div>
            </div>

            <div className="tl-main">
              <div className="tl-price-wrap">
                <div className="tl-currency">₹</div>
                <div className="tl-price">1</div>
                <div className="tl-period">for 7 days</div>
              </div>

              <ul className="tl-single-features">
                <li>✅ Get all premium features</li>
                <li>📊 Dashboard access — Track visibility and actions</li>
                <li>💬 Review replies — Manage & respond quickly</li>
                <li>📅 Post scheduling — Create, schedule, repeat posts</li>
              </ul>

              <button className="tl-cta" onClick={async () => { setShowPlanSelector(true); }}>
                {loading ? 'Processing...' : 'Start Trial in Just ₹1'}
              </button>
              {result && result.success && <div className="tl-success">✅ Trial started — you have access to premium features for 7 days.</div>}
              {result && !result.success && <div className="tl-error">⚠️ {result.error || 'Payment failed. Please try again.'}</div>}
              <div className="tl-micro">No risk • Cancel anytime</div>
            </div>

            <div className="tl-offerstrip compact">⚡ Limited Offer — Start your trial today for just <strong>₹1</strong></div>
          </div>
        </div>
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onClose={(result) => {
          setCheckoutOpen(false);
          if (result?.success) {
            setResult({ success: true });
          } else if (result && !result.success) {
            setResult({ success: false, error: result.error || 'Payment failed' });
          }
        }}
        planId={checkoutProps.planId}
        billingCycle={checkoutProps.billingCycle}
        amount={checkoutProps.amount}
        isTrial={checkoutProps.isTrial}
      />

      <Dialog open={showPlanSelector} onClose={() => setShowPlanSelector(false)} fullWidth maxWidth="md" PaperProps={{ className: 'tl-dialog-paper' }}>
        <DialogTitle className="tl-dialog-title">Choose a Plan</DialogTitle>
        <DialogContent>
          <RadioGroup value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
            <Grid container spacing={2} className="tl-plan-grid" sx={{ mt: 1 }}>
              {plans.map(plan => {
                const sp = serverPrices && serverPrices[plan.id] ? serverPrices[plan.id] : null;
                const base = sp ? (selectedCycle === 'monthly' ? sp.monthly : sp.annually) : pricing[plan.id] || 0;
                let displayPrice = '';
                if (selectedCycle === 'monthly') {
                  displayPrice = `₹1 (trial) → ₹${Number(base).toLocaleString('en-IN')}/mo`;
                } else {
                  displayPrice = `₹1 (trial) → ₹${Number(base).toLocaleString('en-IN')}/yr`;
                }
                return (
                  <Grid item xs={12} sm={4} key={plan.id}>
                    <Paper elevation={6} className={`tl-plan-card ${selectedPlan === plan.id ? 'tl-plan-selected' : ''}`}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div className="title">{plan.title}</div>
                          <div className="price">{displayPrice}</div>
                        </div>
                        <FormControlLabel value={plan.id} control={<Radio />} label="" sx={{ mr: 0 }} />
                      </div>
                      <div className="bullets">
                        {plan.bullets.map((b, i) => (
                          <div key={i}>• {b}</div>
                        ))}
                      </div>
                      <div className="choose-btn">
                        <button className="tl-cta" onClick={() => setSelectedPlan(plan.id)} style={{ padding: '8px 12px', fontSize: 13 }}>
                          {selectedPlan === plan.id ? 'Selected' : 'Choose'}
                        </button>
                      </div>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
          <div style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center', color: '#cfd7ea' }}>
            <div style={{ fontSize: 14 }}>Billing cycle:</div>
            <RadioGroup row value={selectedCycle} onChange={(e) => setSelectedCycle(e.target.value)}>
              <FormControlLabel value="monthly" control={<Radio sx={{ color: '#ffefb8' }} />} label="Monthly" />
              <FormControlLabel value="annually" control={<Radio sx={{ color: '#ffefb8' }} />} label="Yearly" />
            </RadioGroup>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <button className="tl-cta" style={{ background: 'transparent', color: '#fff', boxShadow: 'none', border: '1px solid rgba(255,255,255,0.06)' }} onClick={() => setShowPlanSelector(false)}>Cancel</button>
          <button className="tl-cta" onClick={() => {
            setCheckoutProps({ planId: selectedPlan, billingCycle: selectedCycle, amount: 1, isTrial: true });
            setShowPlanSelector(false);
            setCheckoutOpen(true);
          }}>Continue to Checkout</button>
        </DialogActions>
      </Dialog>
    </>
  );
}
